import { useApolloClient, useLazyQuery } from '@apollo/client'
import CartIcon from '@mui/icons-material/ShoppingCartOutlined'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { isEmpty } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { GetDirectLinksDocument, MediaFragmentFragmentDoc } from '../../../../../../graphql/generated'
import useAlerts from '../../../../hooks/useAlerts'

interface Props {
  ids?: string[]
  isDisabled?: boolean
  color?: 'inherit' | 'primary' | 'secondary'
}

export default function MediaPrintShop({ ids = [], isDisabled = false, color = 'inherit' }: Readonly<Props>) {
  const client = useApolloClient()
  const clientRef = useRef(client)
  const [mediaIds, setMediaIds] = useState<string[]>([])
  const [heicUrls, setHeicUrls] = useState<string[]>([])
  const { createAlert } = useAlerts()
  const [query] = useLazyQuery(GetDirectLinksDocument, {
    // Direct links are signed with an expiring token. Therefore we must refetch to get a new token
    fetchPolicy: 'no-cache',
    onError: () => {
      createAlert('There was a problem retrieving your image. Please try again later.')
    },
    onCompleted: ({ getDirectLinks }) => {
      const urls = getDirectLinks.map((link) => encodeURIComponent(link || ''))
      urls.push(...heicUrls)

      if (!isEmpty(urls)) {
        const url = `https://printshoplab.printshoplab.com/prints/quick-order?source=${urls.join(';')}`
        window.open(url, '_blank')
      }
    }
  })

  useEffect(() => {
    const heics: string[] = []
    const stack: string[] = []

    ids.forEach((id) => {
      const media = clientRef.current.readFragment({ id: `Image:${id}`, fragment: MediaFragmentFragmentDoc })
      if (media?.originalImage.url.toLowerCase().includes('.heic')) {
        // heic files must use a hosting domain link rather than a direct link
        heics.push(encodeURIComponent(media.originalImage.url))
      } else if (!media?.isVideoType) {
        stack.push(id)
      }
    })

    setMediaIds(stack)
    setHeicUrls(heics)
  }, [ids])

  const handleClick = () => {
    const variables = { ids: mediaIds }
    query({ variables })
  }

  return (
    <IconButton
      aria-label="Order Prints"
      disabled={isEmpty(ids) || isDisabled}
      onClick={handleClick}
      color={color}
      data-test="order-prints"
      id="order-prints-button"
    >
      <Tooltip title="Buy Prints" arrow>
        <CartIcon />
      </Tooltip>
    </IconButton>
  )
}
